import * as React from 'react';
import { cn } from '../lib/cn';
import { Label } from './input';

const control = 'w-full rounded-lg border border-line bg-surface px-3 text-[15px] text-ink placeholder:text-ink-3 focus:border-tick focus:outline-none focus:ring-2 focus:ring-tick/25 aria-[invalid=true]:border-bad disabled:bg-canvas disabled:text-ink-3';

/** Label + control + hint/error, wired with aria-describedby. */
export function Field({ id, label, hint, error, children, className }: { id: string; label: React.ReactNode; hint?: React.ReactNode; error?: string | null; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? <p id={`${id}-error`} role="alert" className="text-xs text-bad">{error}</p> : hint ? <p id={`${id}-hint`} className="text-xs text-ink-3">{hint}</p> : null}
    </div>
  );
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...p }, ref) => (
  <textarea ref={ref} className={cn(control, 'min-h-24 py-2', className)} {...p} />
));
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...p }, ref) => (
  <select ref={ref} className={cn(control, 'h-11', className)} {...p} />
));
Select.displayName = 'Select';

/** The one checkbox (tick-ink when ticked); without a label it is a bare box, e.g. a table row's selector (give it an aria-label). */
export function Checkbox({ label, className, ...p }: React.InputHTMLAttributes<HTMLInputElement> & { label?: React.ReactNode }) {
  if (label === undefined) return <input type="checkbox" className={cn('size-4 accent-[var(--color-tick-ink)]', className)} {...p} />;
  return (
    <label className={cn('flex items-start gap-3 text-sm text-ink-2', className)}>
      <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-tick-ink)]" {...p} />
      <span>{label}</span>
    </label>
  );
}
